import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, RotateCcw, Trash2, MoreHorizontal, ListOrdered, Filter, Download } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, Pagination, StatusPill, FilterBuilder, type ColumnDef, type FilterField, type FilterCondition } from '@vnetwork/ui/data';
import {
  Button,
  IconButton,
  Input,
  Skeleton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Tooltip,
  TooltipProvider,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useVodJobs,
  useVodStatuses,
  useRedoVodJob,
  useDeleteVodJob,
} from '@/api/media/vod-jobs';
import type { VodJob } from '@/api/media/vod-jobs-types';
import { jobStatusTone } from '../../shared/job-status-tone';
import { useDebounce } from '../../shared';
import { RefreshButton } from '../../_shared/AnalyticsKit';
import { VodConsoleBreadcrumbs } from '../VodConsoleBreadcrumbs';
import styles from './JobsList.module.css';

interface Props {
  serviceId: string;
}

const PAGE_SIZE = 10;
const fmtDate = (raw?: string) => (raw ? new Date(raw).toLocaleString() : '—');

/** Download the current page of jobs as a CSV file. */
function exportCsv(rows: VodJob[], statusLabelMap: Record<string, string>) {
  const head = ['id', 'name', 'status', 'file_path', 'created_at'];
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const body = rows
    .map((j) => {
      const val = String(j.provider_status ?? j.status ?? '');
      const status = statusLabelMap[val] || (j.status ?? '');
      const filePath =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (j.metadata as any)?.ingestion_config?.file_path ??
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (j.spec as any)?.ingestion_config?.file_path ??
        '';
      return [j.id, j.name, status, filePath, j.created_at].map(esc).join(',');
    })
    .join('\n');
  const blob = new Blob([`${head.join(',')}\n${body}`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `vod-jobs-${rows.length}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function VodJobsList({ serviceId }: Props) {
  const navigate = useNavigate();
  const toast = useToast();
  const base = `/services/vod/${serviceId}`;

  const [page, setPage] = useState(1);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [pendingDelete, setPendingDelete] = useState<VodJob | null>(null);

  // 400ms debounce before filtering — API has no keyword param so filtering is client-side.
  const search = useDebounce(searchInput, 400);

  const { data: statuses } = useVodStatuses(serviceId);

  const statusFilter = useMemo(() => {
    const c = conditions.find((x) => x.field === 'status' && x.value.trim());
    return c?.value.trim();
  }, [conditions]);

  const filterFields: FilterField[] = useMemo(
    () => [
      {
        value: 'status',
        label: 'Status',
        input: 'select',
        operators: [{ value: 'equals', label: 'is' }],
        options: (statuses ?? []).map((s) => ({ value: String(s.value), label: s.name })),
      },
    ],
    [statuses],
  );

  const statusLabelMap = useMemo(
    () =>
      (statuses ?? []).reduce<Record<string, string>>((acc, s) => {
        if (s.value != null) acc[String(s.value)] = s.name;
        return acc;
      }, {}),
    [statuses],
  );

  const { data, isLoading, refetch } = useVodJobs(
    serviceId,
    {
      page,
      limit: PAGE_SIZE,
      status: statusFilter || undefined,
    },
  );

  const redo = useRedoVodJob(serviceId, {
    onSuccess: () => toast.success('Job restarted'),
    onError: () => toast.error('Could not restart job'),
  });

  const remove = useDeleteVodJob(serviceId, {
    onSuccess: () => {
      toast.success('Job deleted');
      setPendingDelete(null);
    },
    onError: () => toast.error('Could not delete job'),
  });

  const jobs = useMemo(() => {
    const all = data?.jobs ?? [];
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter(
      (j) =>
        j.id?.toLowerCase().includes(q) ||
        j.name?.toLowerCase().includes(q),
    );
  }, [data?.jobs, search]);

  const total = data?.total ?? data?.count ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const isSearching = search.trim().length > 0;

  const columns = useMemo<ColumnDef<VodJob, unknown>[]>(
    () => [
      {
        id: 'id',
        header: 'Job ID',
        cell: ({ row }) => {
          const id = row.original.id ?? '';
          return (
            <TooltipProvider>
              <Tooltip content={id} side="top">
                <span
                  className={styles.jobIdCell}
                  role="link"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); navigate(`${base}/jobs/${id}`); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); navigate(`${base}/jobs/${id}`); } }}
                >
                  {id.length > 8 ? `${id.slice(0, 8)}…` : id}
                </span>
              </Tooltip>
            </TooltipProvider>
          );
        },
        size: 110,
      },
      {
        id: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <span className={styles.nameCell}>{row.original.name || '—'}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const val = String(row.original.provider_status ?? row.original.status ?? '');
          const label = statusLabelMap[val] || (row.original.status ?? 'Unknown');
          return <StatusPill tone={jobStatusTone(label)}>{label}</StatusPill>;
        },
        size: 140,
      },
      {
        id: 'file_path',
        header: 'File Path',
        enableSorting: false,
        cell: ({ row }) => {
          // List rows carry ingestion_config in `spec` (jobs created here); only
          // the detail endpoint fills `metadata.ingestion_config`. Try both.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const fp =
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (row.original.metadata as any)?.ingestion_config?.file_path ??
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (row.original.spec as any)?.ingestion_config?.file_path;
          if (!fp) return <span className={styles.emptyCell}>—</span>;
          const short = fp.length > 40 ? `${fp.slice(0, 20)}…${fp.slice(-15)}` : fp;
          return (
            <TooltipProvider>
              <Tooltip content={fp} side="top">
                <span className={styles.monoCell}>{short}</span>
              </Tooltip>
            </TooltipProvider>
          );
        },
      },
      {
        id: 'created_at',
        header: 'Created',
        cell: ({ row }) => (
          <span className={styles.dateCell}>
            {fmtDate(row.original.created_at)}
          </span>
        ),
        size: 170,
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const job = row.original;
          const busy = redo.isPending || remove.isPending;
          const canRedo = job.status === 'failed';
          return (
            <div className={styles.actionsCell} onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton variant="ghost" size="sm" aria-label="Job actions">
                    <MoreHorizontal size={16} />
                  </IconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => navigate(`${base}/jobs/${job.id}`)}>
                    View detail
                  </DropdownMenuItem>
                  {canRedo && (
                    <DropdownMenuItem
                      disabled={busy}
                      onSelect={() => redo.mutate(job.id)}
                    >
                      <RotateCcw size={14} /> Retry
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    tone="danger"
                    disabled={busy}
                    onSelect={() => setPendingDelete(job)}
                  >
                    <Trash2 size={14} /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
        size: 60,
      },
    ],
    [base, navigate, redo, remove.isPending, statusLabelMap],
  );

  // Footer reflects the currently visible row count when a search narrows the page.
  const footerText = useMemo(() => {
    if (isSearching) {
      return jobs.length === 0
        ? 'No matches on this page'
        : `${jobs.length} match${jobs.length !== 1 ? 'es' : ''} on this page`;
    }
    return total > 0 ? `${total} total job${total !== 1 ? 's' : ''}` : undefined;
  }, [isSearching, jobs.length, total]);

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<VodConsoleBreadcrumbs trail={[{ label: 'Jobs' }]} />}
        title={`Jobs${total ? ` (${total})` : ''}`}
        description="Encoding jobs and their processing status. Click a row to view details."
        actions={
          <Button
            startIcon={<Plus size={15} />}
            onClick={() => navigate(`${base}/jobs/create`)}
          >
            Create job
          </Button>
        }
      />

      <Toolbar
        start={
          <Input
            placeholder="Search by ID or name…"
            startSlot={<Search size={14} />}
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
            wrapperClassName={styles.searchInput}
          />
        }
        end={
          <>
            <RefreshButton onRefresh={() => void refetch()} />
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
              <PopoverTrigger asChild>
                <Button variant="secondary" startIcon={<Filter size={15} />}>
                  Filters{conditions.length ? ` (${conditions.length})` : ''}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className={styles.filterPopover}>
                <FilterBuilder
                  fields={filterFields}
                  defaultValue={conditions.length ? conditions : undefined}
                  onApply={(c) => {
                    setConditions(c.filter((x) => x.value.trim()));
                    setPage(1);
                    setFilterOpen(false);
                  }}
                  onClose={() => setFilterOpen(false)}
                />
              </PopoverContent>
            </Popover>
            {conditions.length > 0 && (
              <Button variant="ghost" onClick={() => { setConditions([]); setPage(1); }}>Clear</Button>
            )}
            <Button
              variant="secondary"
              startIcon={<Download size={15} />}
              disabled={jobs.length === 0}
              onClick={() => exportCsv(jobs, statusLabelMap)}
            >
              {isSearching ? 'Export page' : 'Export'}
            </Button>
          </>
        }
      />

      {isLoading ? (
        <div className={styles.skeletonStack}>
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="rect" height={44} />)}
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={jobs}
            getRowId={(r) => r.id}
            onRowClick={(r) => navigate(`${base}/jobs/${r.id}`)}
            emptyState={
              total === 0 && !isSearching ? (
                <EmptyState
                  icon={<ListOrdered size={20} />}
                  title="No jobs yet"
                  description="Create your first encoding job to get started."
                  actions={
                    <Button startIcon={<Plus size={15} />} onClick={() => navigate(`${base}/jobs/create`)}>
                      Create job
                    </Button>
                  }
                />
              ) : isSearching ? (
                'No jobs on this page match your search.'
              ) : (
                'No jobs to display.'
              )
            }
            footer={footerText}
          />
          {pageCount > 1 && (
            <div className={styles.paginationRow}>
              <Pagination
                page={page}
                pageCount={pageCount}
                onPageChange={setPage}
                summary={`Page ${page} of ${pageCount}`}
              />
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete job?"
        description={`Permanently delete job "${pendingDelete?.name || pendingDelete?.id}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        requireText={pendingDelete?.name || pendingDelete?.id}
        loading={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </Container>
  );
}

export default VodJobsList;
